"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Scale, Stethoscope, Landmark, GraduationCap, FileCheck2, Lock, ArrowRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const sectors = [{
  id: "legal",
  title: "Legal practices",
  iconName: "Scale",
  tagline: "Solicitors, chambers, and corporate advisory",
  challenge: "Prospective clients arrive during vulnerable moments; search engines require strict proof of standing.",
  strategy: "I help you build verifiable practitioner bios, jurisdictional topic clusters, and legal analysis that shows clear expertise."
}, {
  id: "healthcare",
  title: "Healthcare & clinical",
  iconName: "Stethoscope",
  tagline: "Private clinics, diagnostic centres, and specialists",
  challenge: "Google evaluates health content under severe Your Money Your Life (YMYL) standards.",
  strategy: "I establish rigorous clinical review workflows, author attribution, and cite verifiable peer-reviewed sources."
}, {
  id: "finance",
  title: "Financial services",
  iconName: "Landmark",
  tagline: "Wealth managers, fintech, and accountancy",
  challenge: "Regulatory compliance strictly limits promissory claims while user scepticism remains very high.",
  strategy: "I design transparent explanatory resources, clear fee frameworks, and factual answers without crossing into unregulated advice."
}, {
  id: "education",
  title: "Education & training",
  iconName: "GraduationCap",
  tagline: "Professional institutes, higher ed, and certified bodies",
  challenge: "Students and corporate buyers need clear accreditation proof before making high-stakes decisions.",
  strategy: "I structure syllabus pathways, institutional credential schemas, and transparent outcome documentation."
}];
const pillars = [{
  title: "Documenting genuine E-E-A-T",
  description: "I map out the actual credentials, career histories, and professional memberships of your specialists so search crawlers recognise verified authorities.",
  metric: "Demonstrated real experience"
}, {
  title: "Compliant content governance",
  description: "Every article, service page, and guide follows strict internal approval flows to meet professional regulatory boundaries without losing search intent.",
  metric: "Zero regulatory ambiguity"
}, {
  title: "Exhaustive topical authority",
  description: "Rather than isolated blog posts, I architect comprehensive knowledge hubs that answer adjacent queries and demonstrate true sector leadership.",
  metric: "Logical knowledge graphs"
}, {
  title: "Buyer reassurance signals",
  description: "Cautious buyers need proof before enquiry. I introduce transparent pricing policies, credentials, and clear engagement terms throughout your user journey.",
  metric: "Trust-first visitor journey"
}];
const checklists = [{
  id: 1,
  title: "Named authors and peer reviewers with direct links to professional bodies"
}, {
  id: 2,
  title: "Regular audit trails for statutory updates and clinical or legal accuracy"
}, {
  id: 3,
  title: "Precise JSON-LD schema for organisations, qualifications, and certified services"
}, {
  id: 4,
  title: "Clear, plain-language disclaimers placed naturally near technical explanations"
}];
export default function RegulatedStrategy() {
  const [activeSector, setActiveSector] = useState("legal");
  const shouldReduceMotion = useReducedMotion();
  const selectedSectorData = sectors.find(s => s.id === activeSector) || sectors[0];
  return <section id="regulatedsectorstrategy" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary border border-primary/20">
            Regulated sector SEO
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default mb-6 leading-tight">
            SEO strategy where trust and compliance cannot be compromised
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you run a practice in law, healthcare, finance, or education, standard SEO shortcuts can damage your standing. I work with you directly to build search authority rooted in verified experience, compliant content practices, and genuine expertise.
          </p>
        </div>

        {/* Sector Navigator Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 items-start">
          {/* Sector Buttons */}
          <div className="lg:col-span-4 flex flex-col gap-3">
            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground px-2">
              Select your sector
            </span>
            {sectors.map((sector, index) => {
            const isSelected = sector.id === activeSector;
            return <div key={sector.id} data-index={index}>
                  <Button type="button" variant="ghost" onClick={() => setActiveSector(sector.id)} className={`w-full h-auto text-left p-4 rounded-xl transition-[color,background-color,border-color,box-shadow] flex items-center justify-between border cursor-pointer ${isSelected ? "bg-card border-primary/40 shadow-sm ring-1 ring-primary/20" : "bg-background border-border hover:bg-muted/50 hover:border-border/80"}`}>
                    <div className="flex items-center gap-3.5">
                      <div className={`size-10 rounded-lg flex items-center justify-center transition-colors shrink-0 ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        {sector.id === "legal" && <Scale className="size-5" />}
                        {sector.id === "healthcare" && <Stethoscope className="size-5" />}
                        {sector.id === "finance" && <Landmark className="size-5" />}
                        {sector.id === "education" && <GraduationCap className="size-5" />}
                      </div>
                      <div>
                        <div className="font-semibold text-foreground text-sm sm:text-base">
                          {sector.title}
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-1">
                          {sector.tagline}
                        </div>
                      </div>
                    </div>
                    <ArrowRight className={`size-4 transition-transform shrink-0 ${isSelected ? "text-primary translate-x-0.5" : "text-muted-foreground/50"}`} />
                  </Button>
                </div>;
          })}
          </div>

          {/* Sector Detail Panel */}
          <div className="lg:col-span-8">
            <Card className="border border-border/80 bg-card shadow-sm rounded-2xl overflow-hidden p-0">
              <div className="p-6 sm:p-8 bg-muted/30 border-b border-border">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                    Direct strategic focus
                  </span>
                  <Badge variant="outline" className="text-xs bg-background text-foreground">
                    Independent consulting
                  </Badge>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 font-default">
                  {selectedSectorData.title}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base">
                  {selectedSectorData.tagline}
                </p>
              </div>

              <CardContent className="p-6 sm:p-8 space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                    <AlertCircle className="size-4 text-accent" />
                    The specific risk &amp; hurdle
                  </h4>
                  <p className="text-foreground text-base leading-relaxed">
                    {selectedSectorData.challenge}
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                    <ShieldCheck className="size-4 text-primary" />
                    My hands-on solution
                  </h4>
                  <p className="text-foreground text-base leading-relaxed">
                    {selectedSectorData.strategy}
                  </p>
                </div>

                <div className="bg-background rounded-xl p-5 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
                  <div className="space-y-1">
                    <div className="text-sm font-semibold text-foreground">
                      Need a sector-specific audit?
                    </div>
                    <div className="text-xs text-muted-foreground">
                      You review findings directly with me — no account executives or junior staff.
                    </div>
                  </div>
                  <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">
                    <Link to="/ContactPage">
                      Discuss your practice
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 4 Core Pillars */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default mb-3">
              How I handle E-E-A-T for high-scrutiny websites
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Google algorithms place heavier scrutiny on content that influences health, financial stability, legal outcomes, or professional qualifications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar, index) => <div key={pillar.title} data-index={index} className="h-full">
                <Card className="h-full border border-border bg-card/60 hover:bg-card transition-[background-color,border-color,box-shadow] rounded-xl flex flex-col justify-between p-6">
                  <div>
                    <div className="text-xs font-semibold text-accent mb-3 font-mono">
                      0{index + 1}
                    </div>
                    <h4 className="text-lg font-semibold text-foreground mb-3 font-default">
                      {pillar.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {pillar.description}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-border/60">
                    <span className="text-xs font-medium text-foreground/80 flex items-center gap-1.5">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      {pillar.metric}
                    </span>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Compliant Governance Checklist Callout */}
        <div className="rounded-2xl bg-muted/40 border border-border p-8 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 space-y-4">
              <Badge variant="secondary" className="text-xs text-primary font-medium">
                Compliant architecture
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default leading-tight">
                Clear processes keep your marketing safe
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                I do not recommend mass automated copy or questionable outreach tactics. In regulated fields, technical precision and factual clarity yield better, safer long-term rankings.
              </p>
              <div className="pt-2">
                <Button asChild variant="outline" className="border-border hover:bg-background">
                  <Link to="/SeoAuditsPage">
                    Explore my technical audits
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {checklists.map((item, index) => <div key={item.id} data-index={index} className="bg-card border border-border rounded-xl p-4 flex items-start gap-3 shadow-xs">
                    <div className="mt-0.5 size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Lock className="size-3" />
                    </div>
                    <span className="text-xs sm:text-sm text-foreground/90 font-medium leading-normal">
                      {item.title}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Direct Access Statement */}
        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Every audit and recommendation document is produced directly by me — Yassine Essaadi. I personally evaluate your technical architecture, content compliance, and topical authority to deliver clear, prioritised steps tailored to your sector.
          </p>
        </div>
      </div>
    </section>;
}